import React, { useEffect, useRef } from 'react';
import { shortTurnModes } from '@/components/config_forms/short_turn/utils/surchargeMethods';
import { surchargeMethodDescriptions } from '../utils/loadDescriptions';
import { SurchargeMethodSelector as SharedSurchargeMethodSelector, type SurchargeMethod } from '../../shared/surcharge/SurchargeMethodSelector';
import { useShortTurnConfig } from '../ShortTurnConfigForm';
import type { RateMultiplier, ShortTurnConfig as ShortTurnConfigType } from '@/types';
import { ShiftTimelineComparison } from '../../shared/help-graphics/ShiftTimelineComparison';
import type { ShiftData, RestConnector, MethodTimeline } from '../../shared/help-graphics/ShiftTimelineComparison';
import { HourlyPremiumBreakdown } from '../../shared/help-graphics/HourlyPremiumBreakdown';
import type { PremiumBracket } from '../../shared/help-graphics/HourlyPremiumBreakdown';
import { FixedPremiumBreakdown } from '../../shared/help-graphics/FixedPremiumBreakdown';
import type { FixedPremiumShift } from '../../shared/help-graphics/FixedPremiumBreakdown';
import { FloorComparisonTimeline } from '../../shared/help-graphics/FloorComparisonTimeline';
import { OffsetComparisonTimeline } from '../../shared/help-graphics/OffsetComparisonTimeline';
import { RestGapConnector } from '../../shared/help-graphics/RestGapConnector';
import type { FloorBracket } from '../../shared/help-graphics/FloorComparisonTimeline';
import { helpRegistry, type HelpEntry } from '@/components/ui/help-registry';
import { formatMultiplier } from '@/utils/rateCalculations';
import { MethodControlPanel } from './MethodControlPanel';
import { NumberInput } from '@/components/ui/number-input';
import { NumberStepper } from '@/components/ui/number-stepper';
import { HelpInfoCallout } from './HelpInfoCallout';
import { cn } from '@/lib/utils';
import { CONTROL_ROW_BODY_CLASS, CONTROL_ROW_LABEL_CLASS } from '../../shared/ControlRow';
import { SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING } from '../../shared/help-graphics/helpSheetOpticalSpacing';
import { getOvertimeLadderInfo } from '@/hooks/useOvertimeLadderInfo';
import { buildPreviewLadder } from '../../shared/buildPreviewLadder';

type MethodInjectableMap = Record<string, Record<string, React.ReactNode>>;

interface BuildShortTurnMethodInjectablesArgs {
  markdownVariables: Record<string, unknown>;
  shortTurnConfig: ShortTurnConfigType;
  ratedRoleNames: string[];
  activeRoleIndex: number;
  rateMultipliers: RateMultiplier[];
  context: 'user' | 'client';
  highestOvertimeRateOverride?: number | null;
  onRoleCycle?: () => void;
  onShortTurnConfigChange: (updates: Partial<ShortTurnConfigType>) => void;
}

export function buildShortTurnMethodInjectables({
  markdownVariables,
  shortTurnConfig,
  ratedRoleNames,
  activeRoleIndex,
  rateMultipliers,
  context,
  highestOvertimeRateOverride,
  onRoleCycle,
  onShortTurnConfigChange,
}: BuildShortTurnMethodInjectablesArgs): MethodInjectableMap | undefined {
  const sustained = markdownVariables.sustained_timeline as { shifts: ShiftData[]; connectors: RestConnector[] } | undefined;
  const accelerated = markdownVariables.bump_up_timeline as { shifts: ShiftData[]; connectors: RestConnector[] } | undefined;
  const fixed = markdownVariables.fixed_timeline as { shifts: ShiftData[]; connectors: RestConnector[] } | undefined;
  const floor = markdownVariables.floor_timeline as { shifts: ShiftData[]; connectors: RestConnector[] } | undefined;
  const floorComparisonData = markdownVariables.floor_comparison_data as {
    brackets: FloorBracket[];
    floorMultiplier: number;
    floorLabel: string;
  } | undefined;
  const offsetComparisonData = markdownVariables.offset_comparison_data as {
    brackets: { flex: number; naturalMultiplier: number; naturalLabel: string }[];
    effectiveLadder: { multiplier: number; label: string }[];
    offsetIndex: number;
  } | undefined;
  const premiumData = markdownVariables.premium_breakdown_data as {
    shiftHours: number;
    violationHours: number;
    surchargeAppliesToShortedHoursOnly: boolean;
    premiumType: 'percentage' | 'fixed';
    premiumValue: number;
    premiumBasis: 'base_rate' | 'ot_inclusive';
    brackets: PremiumBracket[];
    basePay: number;
    surchargedPay: number;
    surchargeAmount: number;
    baseBuckets: import('@/utils/rateCalculations').PayBucket[];
    surchargedBuckets: import('@/utils/rateCalculations').PayBucket[];
    roleContext: string;
    actualRestHours: number;
  } | undefined;

  const handleMethodChange = (methodValue: string) => {
    onShortTurnConfigChange({ surchargeType: methodValue as ShortTurnConfigType['surchargeType'] });
  };

  const fixedPremiumConsistencyCallout = (
    <HelpInfoCallout
      title="Fixed Premium"
      className={SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.fixedPremium}
    >
      Unlike rate-based methods, the surcharge amount doesn&apos;t change based on
      hours worked or which overtime tier you&apos;re in.
    </HelpInfoCallout>
  );

  const hourlyPremiumTypeCallout = (
    <HelpInfoCallout title="Premium Type">
      The premium can be a fixed dollar amount or a percentage of your hourly rate.
    </HelpInfoCallout>
  );

  const hourlyPremiumBasisCallout = (
    <HelpInfoCallout title="Overtime">
      Enable <strong className="font-medium text-primary-text">Scale With Overtime</strong>{' '}
      to multiply the hourly premium by the active overtime tier.
    </HelpInfoCallout>
  );

  const premiumInjectable = {
    ...(premiumData ? {
      premium_breakdown: (
        <HourlyPremiumBreakdown
          shiftHours={premiumData.shiftHours}
          violationHours={premiumData.violationHours}
          surchargeAppliesToShortedHoursOnly={premiumData.surchargeAppliesToShortedHoursOnly}
          premiumType={premiumData.premiumType}
          premiumValue={premiumData.premiumValue}
          premiumBasis={premiumData.premiumBasis}
          brackets={premiumData.brackets}
          basePay={premiumData.basePay}
          surchargedPay={premiumData.surchargedPay}
          surchargeAmount={premiumData.surchargeAmount}
          baseBuckets={premiumData.baseBuckets}
          surchargedBuckets={premiumData.surchargedBuckets}
          roleContext={premiumData.roleContext}
          onRoleCycle={onRoleCycle}
          roleKey={activeRoleIndex}
          onBasisChange={(basis) => onShortTurnConfigChange({ premiumBasis: basis })}
          onTypeChange={(type) => onShortTurnConfigChange({ premiumType: type })}
          onPremiumValueChange={(v) => onShortTurnConfigChange({ premiumValue: v ?? undefined })}
          actualRestHours={premiumData.actualRestHours}
          calloutsAboveControls={(
            <>
              {hourlyPremiumTypeCallout}
              {hourlyPremiumBasisCallout}
            </>
          )}
        />
      ),
    } : {}),
  };

  const fixedPremiumData = markdownVariables.fixed_premium_breakdown_data as {
    shifts: FixedPremiumShift[];
    surchargeAmount: number;
    thresholdHours: number;
    roleContext: string;
  } | undefined;

  const fixedPremiumInjectable = fixedPremiumData ? {
    fixed_premium_breakdown: (
      <FixedPremiumBreakdown
        shifts={fixedPremiumData.shifts}
        surchargeAmount={fixedPremiumData.surchargeAmount}
        thresholdHours={fixedPremiumData.thresholdHours}
        consistencyCallout={fixedPremiumConsistencyCallout}
        roleContext={fixedPremiumData.roleContext}
        onRoleCycle={onRoleCycle}
        roleKey={activeRoleIndex}
        onSurchargeAmountChange={(v) => onShortTurnConfigChange({ shortTurnFixedSurchargeAmount: v ?? undefined })}
      />
    ),
  } : {};

  if (!sustained || !accelerated || !fixed || !floor) {
    return {
      hourly_premium: premiumInjectable,
      ...(fixedPremiumData ? { fixed_dollar: fixedPremiumInjectable } : {}),
    } as MethodInjectableMap;
  }

  // Build sub-badge options for fixed start multiplier selector
  const nonStraightTime = rateMultipliers.filter(m => m.code !== 'STRAIGHT_TIME');
  const {
    highestOvertimeRate,
    highestOvertimeTierName,
    maxExtendedTiers,
  } = getOvertimeLadderInfo(rateMultipliers, highestOvertimeRateOverride);
  const fixedSubBadge = nonStraightTime.length >= 2 ? {
    options: nonStraightTime.map(m => ({ label: formatMultiplier(m), value: m.code })),
    activeValue: shortTurnConfig.floorTierCode ?? nonStraightTime[0].code,
    onSelect: (value: string) => onShortTurnConfigChange({ floorTierCode: value }),
  } : undefined;

  const allMethods: MethodTimeline[] = [
    { methodName: 'Carry Over', methodValue: 'carry_over', helpFieldName: 'Short Turn - Carry Over', shifts: sustained.shifts, connectors: sustained.connectors },
    { methodName: 'Bump Up', methodValue: 'bump_up', helpFieldName: 'Short Turn - Bump Up', shifts: accelerated.shifts, connectors: accelerated.connectors },
    {
      methodName: 'Offset Overtime', methodValue: 'offset_overtime', helpFieldName: 'Short Turn - Offset Overtime',
      shifts: [{ ...fixed.shifts[fixed.shifts.length - 1], label: 'Shift', endRate: undefined }],
      connectors: fixed.connectors.slice(1),
      subBadgeOptions: fixedSubBadge?.options,
      activeSubBadgeValue: fixedSubBadge?.activeValue,
      onSubBadgeSelect: fixedSubBadge?.onSelect,
    },
    { methodName: 'Guaranteed Rate', methodValue: 'guaranteed_rate', helpFieldName: 'Short Turn - Guaranteed Rate', shifts: [{ ...floor.shifts[floor.shifts.length - 1], label: 'Shift', endRate: undefined }], connectors: floor.connectors.slice(1) },
  ];


  const hasWeeklyOvertime = rateMultipliers.some(m =>
    m.code !== 'STRAIGHT_TIME' &&
    m.defaultPerWeekThresholdHours != null &&
    m.defaultPerWeekThresholdHours > 0
  );

  const carryOverAdditionalPayCallout = (
    <HelpInfoCallout title="Tier Bump">
      If the previous shift ended at Straight Time, Carry Over starts the surcharge
      at the next tier so adjusted pay stays above normal pay.
    </HelpInfoCallout>
  );

  const carryOverWeeklyOvertimeCallout = hasWeeklyOvertime ? (
    <HelpInfoCallout title="Weekly Overtime">
      If weekly overtime reaches or surpasses the carry-over tier, no surcharge
      applies — the surcharge period is paid at the greater of the carry-over tier or the
      active overtime rate.
    </HelpInfoCallout>
  ) : null;

  const bumpUpWeeklyOvertimeCallout = hasWeeklyOvertime ? (
    <HelpInfoCallout title="Weekly Overtime">
      If weekly overtime reaches or surpasses the bumped tier, no surcharge
      applies — the surcharge period is paid at the greater of the bumped tier or the
      active overtime rate.
    </HelpInfoCallout>
  ) : null;

  const fixedStartConsistencyCallout = (
    <HelpInfoCallout
      title="Offset Overtime"
      className={SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.fixedStart}
    > 
      Always adds the configured <strong>Overtime Offset</strong> on top of the active overtime tier,
      regardless of the previous shift&apos;s length or ending rate.
    </HelpInfoCallout>
  );

  const guaranteedRateConsistencyCallout = (
    <HelpInfoCallout
      title="Guaranteed Rate"
      className={cn(SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.guaranteedRate, 'mb-3')}
    >
      Every hour in the surcharge period is paid at whichever is higher: your
      natural overtime rate or the guaranteed rate.
    </HelpInfoCallout>
  );

  const renderSurchargeScopeCallout = (topSpacingClass = SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.carryOver) => (
    <HelpInfoCallout title="Surcharge Period" className={topSpacingClass}>
      {shortTurnConfig.surchargeAppliesToShortedHoursOnly
        ? 'Shorted Hours is proportional - the less rest you miss, the smaller the surcharge.'
        : 'Full Shift results in a higher surcharge since every hour is upgraded.'}
    </HelpInfoCallout>
  );

  const carryOverFooter = (
    <div className="space-y-2">
      {carryOverAdditionalPayCallout}
      {carryOverWeeklyOvertimeCallout}
    </div>
  );
  const bumpUpFooter = bumpUpWeeklyOvertimeCallout;
  const offsetOvertimeFooter = fixedStartConsistencyCallout;

  const makeTimeline = (
    methodName: string,
    shifts: ShiftData[],
    connectors: RestConnector[],
    footer?: React.ReactNode,
    expandedSurchargedShiftCallout?: React.ReactNode,
  ) => {
    const shortedHours = connectors.find((connector) => connector.triggered && connector.shortedHours != null)?.shortedHours;
    const resolvedSurchargePeriodLabel = shortTurnConfig.surchargeAppliesToShortedHoursOnly
      ? (shortedHours != null ? `${shortedHours}hr Shorted` : 'Shorted Hours')
      : 'Full Shift';
    const triggeredConnectorLabel = shortTurnConfig.surchargeAppliesToShortedHoursOnly
      ? undefined
      : 'Short Turn';

    return (
    <ShiftTimelineComparison
      shifts={shifts}
      connectors={connectors}
      methodName={methodName}
      allMethods={allMethods}
      onMethodChange={handleMethodChange}
      showMethodHeader={false}
      compactFirstShift
      showCollapsedDuration={false}
      showExpandHint
      surchargePeriodLabel={resolvedSurchargePeriodLabel}
      triggeredConnectorLabel={triggeredConnectorLabel}
      roleOptions={ratedRoleNames}
      activeRoleIndex={activeRoleIndex}
      onRoleCycle={onRoleCycle}
      footer={footer}
      expandedSurchargedShiftCallout={expandedSurchargedShiftCallout}
    />
    );
  };

  const methodControls = (
    <MethodControlPanel
      extendedOvertimeEnabled={shortTurnConfig.extendedOvertimeEnabled ?? false}
      onExtendedOvertimeChange={(v) => onShortTurnConfigChange({ extendedOvertimeEnabled: v })}
      extendedOvertimeTiers={shortTurnConfig.extendedOvertimeTiers ?? []}
      highestOvertimeRate={highestOvertimeRate}
      highestOvertimeTierName={highestOvertimeTierName}
      editableExtendedTiers={shortTurnConfig.extendedOvertimeTiers ?? []}
      onEditableExtendedTiersChange={(tiers) => onShortTurnConfigChange({ extendedOvertimeTiers: tiers })}
      editableExtendedTiersContext={context}
      editableExtendedTiersMax={maxExtendedTiers}
    />
  );

  const bumpUpMethodControls = (
    <MethodControlPanel
      extendedOvertimeEnabled={shortTurnConfig.extendedOvertimeEnabled ?? false}
      onExtendedOvertimeChange={(v) => onShortTurnConfigChange({ extendedOvertimeEnabled: v })}
      extendedOvertimeTiers={shortTurnConfig.extendedOvertimeTiers ?? []}
      highestOvertimeRate={highestOvertimeRate}
      highestOvertimeTierName={highestOvertimeTierName}
      editableExtendedTiers={shortTurnConfig.extendedOvertimeTiers ?? []}
      onEditableExtendedTiersChange={(tiers) => onShortTurnConfigChange({ extendedOvertimeTiers: tiers })}
      editableExtendedTiersContext={context}
      editableExtendedTiersMax={maxExtendedTiers + 1}
    />
  );

  const fixedOffsetLadder = buildPreviewLadder(rateMultipliers);
  const fixedOffsetMaxOffset = Math.max(1, fixedOffsetLadder.length - 1);
  const fixedOffsetIsDisabled = fixedOffsetLadder.length <= 1;
  const fixedOffsetAmount = shortTurnConfig.overtimeOffset ?? 1;
  const fixedStartControl = (
    <div
      className="px-3 py-2 grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 items-center sm:flex sm:items-center sm:justify-between sm:gap-4"
      data-anim-audit="short-turn-fixed-start-help-overtime-offset-row"
    >
      <div className="min-w-0">
        <span className={CONTROL_ROW_LABEL_CLASS}>
          Overtime Offset
        </span>
        <p className={cn(CONTROL_ROW_BODY_CLASS, "hidden sm:block")}>
          Number of rate ladder steps to shift upward on a short-turn shift.
        </p>
      </div>
      <div className="flex flex-col items-end">
        <NumberStepper
          ariaLabel="Short-turn overtime offset"
          label="Short-turn overtime offset"
          value={fixedOffsetAmount}
          min={1}
          max={fixedOffsetMaxOffset}
          disabled={fixedOffsetIsDisabled}
          onChange={(next) => onShortTurnConfigChange({ overtimeOffset: next })}
        />
      </div>
      <p className={cn(CONTROL_ROW_BODY_CLASS, "col-span-2 sm:hidden")}>
        Number of rate ladder steps to shift upward on a short-turn shift.
      </p>
    </div>
  );

  const fixedStartMethodControls = (
    <MethodControlPanel
      extendedOvertimeEnabled={shortTurnConfig.extendedOvertimeEnabled ?? false}
      onExtendedOvertimeChange={(v) => onShortTurnConfigChange({ extendedOvertimeEnabled: v })}
      extendedOvertimeTiers={shortTurnConfig.extendedOvertimeTiers ?? []}
      highestOvertimeRate={highestOvertimeRate}
      highestOvertimeTierName={highestOvertimeTierName}
      editableExtendedTiers={shortTurnConfig.extendedOvertimeTiers ?? []}
      onEditableExtendedTiersChange={(tiers) => onShortTurnConfigChange({ extendedOvertimeTiers: tiers })}
      editableExtendedTiersContext={context}
      editableExtendedTiersMax={maxExtendedTiers}
      leadingControl={fixedStartControl}
    />
  );

  // Guaranteed Rate floor: preset multiplier or custom value
  const floorMultipliers = [...nonStraightTime]
    .filter((m): m is RateMultiplier & { defaultMultiplier: number; name: string } => typeof m.defaultMultiplier === 'number')
    .sort((a, b) => a.defaultMultiplier - b.defaultMultiplier);
  const isCustomFloor = shortTurnConfig.floorTierCode == null;
  const activeFloorValue = shortTurnConfig.floorTierCode ?? '';
  const customFloorValue = Number.isFinite(shortTurnConfig.floorTierCustomValue)
    ? shortTurnConfig.floorTierCustomValue!
    : null;

  const renderFloorInput = () => (
    <NumberInput
      aria-label="Guaranteed rate multiplier"
      unit="×"
      unitPosition="right"
      size="xs"
      touchTarget
      value={customFloorValue ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        if (v === '') {
          onShortTurnConfigChange({ floorTierCode: null, floorTierCustomValue: null });
          return;
        }
        const parsed = parseFloat(v);
        if (!Number.isFinite(parsed) || parsed <= 1) return;
        const clamped = Math.min(9.99, parsed);
        onShortTurnConfigChange({ floorTierCode: null, floorTierCustomValue: clamped });
      }}
      minValue={1.01}
      maxValue={9.99}
      step={0.25}
      placeholder="1.5"
      className="text-right transition-none"
    />
  );

  const floorControl = (
    <div
      className="px-3 py-2 space-y-3 md:grid md:grid-cols-2 md:items-center md:gap-4 md:space-y-0"
      data-anim-audit="short-turn-guaranteed-rate-help-row"
    >
      {/* Mobile layout: preserve existing arrangement */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 md:hidden">
        <div className="min-w-0">
          <span className={CONTROL_ROW_LABEL_CLASS}>Guaranteed Rate</span>
          <p className={CONTROL_ROW_BODY_CLASS}>The minimum rate multiplier in effect during the surcharge period.</p>
        </div>
        {isCustomFloor && renderFloorInput()}
      </div>
      {/* Desktop layout: mirror config form field structure */}
      <div className="hidden md:block min-w-0">
        <div className="flex items-center gap-2">
          <span className={CONTROL_ROW_LABEL_CLASS}>
            Guaranteed Rate
          </span>
          {isCustomFloor && <div className="ml-auto">{renderFloorInput()}</div>}
        </div>
        <p className={CONTROL_ROW_BODY_CLASS}>
          The minimum rate multiplier in effect during the surcharge period.
        </p>
      </div>
      {/* Ladder cards full-width below (mobile) and right column (desktop) */}
      <div className="w-full space-y-2 md:col-start-2" data-anim-audit="short-turn-guaranteed-rate-help-rate-list-wrap">
        {floorMultipliers.length > 0 ? floorMultipliers.map((multiplier) => {
          const isActive = multiplier.code === activeFloorValue;
          return (
            <button
              key={multiplier.code}
              type="button"
              onClick={() => onShortTurnConfigChange({ floorTierCode: multiplier.code })}
              className={cn(
                'grid w-full min-h-11 grid-cols-[56px_1fr_auto] items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
                isActive
                  ? 'border-primary/45 bg-primary/[0.08]'
                  : 'border-border/60 bg-surface-1 dark:bg-transparent hover:border-primary/30',
              )}
            >
              <span className={cn(
                'font-mono text-sm font-bold tabular-nums text-left',
                isActive ? 'text-primary-text' : 'text-muted-foreground',
              )}>
                {`${multiplier.defaultMultiplier}x`}
              </span>
              <span className={cn(
                'font-heading text-[13px] uppercase tracking-wide truncate text-left',
                isActive ? 'text-foreground' : 'text-muted-foreground',
              )}>
                {multiplier.name}
              </span>
              {isActive ? (
                <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-primary">
                  <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                </span>
              ) : (
                <span className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
              )}
            </button>
          );
        }) : (
          <p className="text-xs font-mono text-muted-foreground italic text-center">
            No overtime tiers available.
          </p>
        )}
        <button
          type="button"
          onClick={() => onShortTurnConfigChange({ floorTierCode: null })}
          className={cn(
            'grid w-full min-h-11 grid-cols-[56px_1fr_auto] items-center gap-2 px-3 py-2 rounded-lg border transition-colors',
            isCustomFloor
              ? 'border-primary/45 bg-primary/[0.08]'
              : 'border-border/60 bg-surface-1 dark:bg-transparent hover:border-primary/30',
          )}
        >
          <span className={cn(
            'font-mono text-sm font-bold tabular-nums text-left',
            isCustomFloor ? 'text-primary-text' : 'text-muted-foreground',
          )}>
            {isCustomFloor && customFloorValue != null ? `${customFloorValue}x` : '—'}
          </span>
          <span className={cn(
            'font-heading text-[13px] uppercase tracking-wide truncate text-left',
            isCustomFloor ? 'text-foreground' : 'text-muted-foreground',
          )}>
            Custom
          </span>
          {isCustomFloor ? (
            <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-primary">
              <span className="h-2.5 w-2.5 rounded-full bg-primary" />
            </span>
          ) : (
            <span className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
          )}
        </button>
      </div>
    </div>
  );

  const floorMethodControls = (
    <MethodControlPanel
      extendedOvertimeEnabled={shortTurnConfig.extendedOvertimeEnabled ?? false}
      onExtendedOvertimeChange={(v) => onShortTurnConfigChange({ extendedOvertimeEnabled: v })}
      extendedOvertimeTiers={shortTurnConfig.extendedOvertimeTiers ?? []}
      highestOvertimeRate={highestOvertimeRate}
      highestOvertimeTierName={highestOvertimeTierName}
      showExtendedOvertimeControl={false}
      leadingControl={floorControl}
    />
  );

  return {
    carry_over: {
      shift_timeline: <>{makeTimeline('Carry Over', sustained.shifts, sustained.connectors, carryOverFooter, renderSurchargeScopeCallout())}{methodControls}</>,
    },
    bump_up: {
      shift_timeline: <>{makeTimeline('Bump Up', accelerated.shifts, accelerated.connectors, bumpUpFooter, renderSurchargeScopeCallout())}{bumpUpMethodControls}</>,
    },
    offset_overtime: {
      shift_timeline: <>
        {offsetComparisonData && (
          <div className="mb-4">
            <OffsetComparisonTimeline
              brackets={offsetComparisonData.brackets}
              effectiveLadder={offsetComparisonData.effectiveLadder}
              offsetIndex={offsetComparisonData.offsetIndex}
            />
          </div>
        )}
        {fixed.connectors[0]?.triggered && (
          <div className="mb-2">
            <RestGapConnector
              hours={fixed.connectors[0].hours}
              labelText={shortTurnConfig.surchargeAppliesToShortedHoursOnly
                ? (fixed.connectors[0].shortedHours != null ? `${fixed.connectors[0].shortedHours}hr Shorted` : undefined)
                : 'Short Turn'}
              triggered
              className="pb-0"
            />
          </div>
        )}
        {makeTimeline(
          'Offset Overtime',
          [{ ...fixed.shifts[fixed.shifts.length - 1], label: 'Shift', endRate: undefined }],
          fixed.connectors.slice(1),
          offsetOvertimeFooter,
          renderSurchargeScopeCallout(SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.fixedStart),
        )}
        {fixedStartMethodControls}
      </>,
    },
    guaranteed_rate: {
      shift_timeline: <>
        {floorComparisonData && (
          <div className="mb-4">
            <FloorComparisonTimeline
              brackets={floorComparisonData.brackets}
              floorMultiplier={floorComparisonData.floorMultiplier}
              floorLabel={floorComparisonData.floorLabel}
            />
          </div>
        )}
        {floor.connectors[0]?.triggered && (
          <div className="mb-2">
            <RestGapConnector
              hours={floor.connectors[0].hours}
              labelText={shortTurnConfig.surchargeAppliesToShortedHoursOnly
                ? (floor.connectors[0].shortedHours != null ? `${floor.connectors[0].shortedHours}hr Shorted` : undefined)
                : 'Short Turn'}
              triggered
              className="pb-0"
            />
          </div>
        )}
        {makeTimeline(
          'Guaranteed Rate',
          [{ ...floor.shifts[floor.shifts.length - 1], label: 'Shift', endRate: undefined }],
          floor.connectors.slice(1),
          guaranteedRateConsistencyCallout,
          renderSurchargeScopeCallout(SHORT_TURN_HELP_SHEET_CALLOUT_TOP_SPACING.guaranteedRate),
        )}
        {floorMethodControls}
      </>,
    },
    hourly_premium: premiumInjectable,
    fixed_dollar: fixedPremiumInjectable,
  };
}

export const SurchargeMethodSelector: React.FC = () => {
  const {
    shortTurnConfig,
    onShortTurnConfigChange,
    hasAdditionalMultipliers,
    hasMultipliersWithThresholds,
    surchargeTypeError,
    noMultiplierSelectedError,
    customizedFields = {},
    context = 'user',
    markdownVariables,
    ratedRoleNames,
    activeRoleIndex,
    setExampleRoleIndex,
    rateMultipliers,
    highestOvertimeRate: highestOvertimeRateOverride,
  } = useShortTurnConfig();

  const methodInjectables = React.useMemo(() => {
    const cycleRole = ratedRoleNames.length > 1
      ? () => setExampleRoleIndex(prev => ((prev ?? activeRoleIndex) + 1) % ratedRoleNames.length)
      : undefined;

    return buildShortTurnMethodInjectables({
      markdownVariables,
      shortTurnConfig,
      ratedRoleNames,
      activeRoleIndex,
      rateMultipliers,
      context,
      highestOvertimeRateOverride,
      onRoleCycle: cycleRole,
      onShortTurnConfigChange,
    });
  }, [markdownVariables, onShortTurnConfigChange, ratedRoleNames, activeRoleIndex, setExampleRoleIndex, rateMultipliers, context, highestOvertimeRateOverride, shortTurnConfig]);

  // Register all offset methods in the help registry so navigateHelp can
  // resolve them even when only the selected method's HelpButton is mounted.
  const offsetEntries = React.useMemo(() => {
    if (!methodInjectables) return null;
    const methodMap: Record<string, { value: string; label: string }> = {
      carry_over: { value: 'carry_over', label: 'Carry Over' },
      bump_up: { value: 'bump_up', label: 'Bump Up' },
      offset_overtime: { value: 'offset_overtime', label: 'Offset Overtime' },
      guaranteed_rate: { value: 'guaranteed_rate', label: 'Guaranteed Rate' },
      hourly_premium: { value: 'hourly_premium', label: 'Hourly Premium' },
      fixed_dollar: { value: 'fixed_dollar', label: 'Fixed Premium' },
    };
    return Object.entries(methodMap).map(([key, { label }]) => ({
      fieldName: `Short Turn - ${label}`,
      entry: {
        content: (surchargeMethodDescriptions as Record<string, string>)[key] || '',
        variables: markdownVariables,
        injectableComponents: (methodInjectables as Record<string, Record<string, React.ReactNode>>)[key],
      } as HelpEntry,
    }));
  }, [methodInjectables, markdownVariables]);

  const registryRefs = useRef<Map<string, React.MutableRefObject<HelpEntry>>>(new Map());

  useEffect(() => {
    if (!offsetEntries) return;
    const refs = registryRefs.current;
    for (const { fieldName, entry } of offsetEntries) {
      let ref = refs.get(fieldName);
      if (!ref) {
        ref = { current: entry };
        refs.set(fieldName, ref);
      } else {
        ref.current = entry;
      }
      // Only register if not already registered by a mounted HelpButton
      if (!helpRegistry.has(fieldName)) {
        helpRegistry.set(fieldName, ref);
      }
    }
    return () => {
      for (const { fieldName } of offsetEntries) {
        const ref = refs.get(fieldName);
        // Only unregister if we own it (not a mounted HelpButton)
        if (ref && helpRegistry.get(fieldName) === ref) {
          helpRegistry.delete(fieldName);
        }
      }
    };
  }, [offsetEntries]);

  const methods: SurchargeMethod[] = shortTurnModes.map(mode => ({
    value: mode.value,
    label: mode.label,
    description: mode.description,
    icon: mode.icon,
    buttonLabel: mode.buttonLabel,
    technicalName: mode.technicalName,
  }));

  const isMethodDisabled = (method: SurchargeMethod): boolean => {
    return (method.value === "offset_overtime" && !hasAdditionalMultipliers) ||
           (method.value === "guaranteed_rate" && !hasAdditionalMultipliers) ||
           ((method.value === "carry_over" || method.value === "bump_up") && !hasMultipliersWithThresholds);
  };

  const getDisabledTooltip = (method: SurchargeMethod): string => {
    if (method.value === "carry_over" || method.value === "bump_up") {
      return "Add at least one additional rate multiplier with a per-shift or per-week threshold to enable this mode.";
    }
    return "Add at least one additional rate multiplier to enable this mode.";
  };

  const errorMessage = surchargeTypeError
    ? "Please select a Short-Turn Surcharge Method"
    : noMultiplierSelectedError
    ? "Please enter an offset amount for Offset Overtime mode"
    : undefined;

  return (
    <div>
      <SharedSurchargeMethodSelector
        id="short_turn_surcharge_type"
        title="Short-Turn Surcharge Method"
        methods={methods}
        selectedValue={shortTurnConfig.surchargeType || ''}
        onValueChange={(value) => onShortTurnConfigChange({ surchargeType: value as ShortTurnConfigType['surchargeType'] })}
        methodDescriptions={surchargeMethodDescriptions}
        isMethodDisabled={isMethodDisabled}
        getDisabledTooltip={getDisabledTooltip}
        markdownVariables={markdownVariables}
        methodInjectables={methodInjectables}
        columns={6}
        isCustomized={customizedFields.short_turn_surcharge_type}
        context={context}
        helpFieldNamePrefix="Short Turn"
        errorElement={ (surchargeTypeError || noMultiplierSelectedError) ? (
          <div
            className="text-xs text-destructive"
            data-testid="short_turn_surcharge_type-error"
          >
            {errorMessage}
          </div>
        ) : undefined}
      />
    </div>
  );
};
